/**
 * Confirmations: a modal ConfirmDialog (resign, leave) and a lightweight
 * ConfirmPopover anchored to its trigger (pass). Both are named by their title
 * and described by their body (the consequence), trap focus, close on Escape
 * and restore focus to whatever opened them.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { focusIsLost, focusMain } from '../lib/focus';
import { Modal, trapTab } from './Modal';
import './ConfirmDialog.css';

interface ConfirmBase {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmBase) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const bodyId = useId();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      role="alertdialog"
      describedBy={body ? bodyId : undefined}
      initialFocus={cancelRef}
      footer={
        <>
          <button ref={cancelRef} type="button" className="btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={clsx('btn', tone === 'danger' ? 'btn--danger' : 'btn--primary')}
            onClick={onConfirm}
            aria-disabled={busy || undefined}
          >
            {busy && <span className="btn__spinner" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </>
      }
    >
      {body && (
        <div id={bodyId} className="confirm__body">
          {body}
        </div>
      )}
    </Modal>
  );
}

interface PopoverProps extends ConfirmBase {
  anchorRef: RefObject<HTMLElement | null>;
}

export function ConfirmPopover(props: PopoverProps) {
  return createPortal(<AnimatePresence>{props.open && <PopoverPanel {...props} />}</AnimatePresence>, document.body);
}

function PopoverPanel({
  anchorRef,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const [pos, setPos] = useState<{ left: number; top: number; arrow: number; below: boolean } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      const el = ref.current;
      if (!anchor || !el) return;
      const a = anchor.getBoundingClientRect();
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const below = a.top - h - 12 < 8;
      const left = Math.min(Math.max(8, a.left + a.width / 2 - w / 2), window.innerWidth - w - 8);
      const top = below ? a.bottom + 12 : a.top - h - 12;
      setPos({ left, top, arrow: a.left + a.width / 2 - left, below });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchorRef]);

  useEffect(() => {
    const anchor = anchorRef.current;
    const panel = ref.current;
    // Whatever had focus when it opened: the trigger, or the board cell a
    // Shift+P shortcut came from.
    const opener = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus({ preventScroll: true });
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor?.contains(t)) return;
      cancelRef.current();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      // Leave focus alone when the user already moved it (an outside click on
      // another control); otherwise opener → trigger → main content.
      if (!focusIsLost() && !panel?.contains(document.activeElement)) return;
      const back = [opener, anchor].find((el) => el && el !== document.body && el.isConnected);
      if (back) back.focus({ preventScroll: true });
      else focusMain();
    };
  }, [anchorRef]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelRef.current();
      return;
    }
    trapTab(e, ref.current);
  };

  return (
    <motion.div
      ref={ref}
      role="alertdialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={body ? bodyId : undefined}
      className={clsx('popover', pos?.below && 'popover--below')}
      style={{
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        ['--arrow-x' as string]: `${pos?.arrow ?? 0}px`,
      }}
      initial={{ opacity: 0, scale: 0.96, y: pos?.below ? -4 : 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
      onKeyDown={onKeyDown}
    >
      <h2 id={titleId} className="popover__title">
        {title}
      </h2>
      {body && (
        <div id={bodyId} className="popover__body">
          {body}
        </div>
      )}
      <div className="popover__actions">
        <button type="button" className="btn btn--sm" onClick={() => cancelRef.current()}>
          {cancelLabel}
        </button>
        <button
          ref={confirmRef}
          type="button"
          className={clsx('btn btn--sm', tone === 'danger' ? 'btn--danger' : 'btn--primary')}
          onClick={onConfirm}
          aria-disabled={busy || undefined}
        >
          {busy && <span className="btn__spinner" aria-hidden="true" />}
          {confirmLabel}
        </button>
      </div>
    </motion.div>
  );
}
